// Financial Freedom Calculator -- the answer cards.
//
// Maps the single unified "God Payload" into three distinct dashboard cards
// (one per user question). While a re-calculation is in flight the whole grid
// dims to 60% opacity (rather than vanishing) because the query uses
// keepPreviousData -- a butter-smooth "live" feel with no loading spinner flash.

import React from 'react';

import type { FinancialFreedomInputs, FinancialFreedomOutputs } from '../types';
import { formatCurrency } from '../utils/currency';

interface ResultsPanelProps {
    data?: FinancialFreedomOutputs;
    inputs: FinancialFreedomInputs;
    isFetching: boolean;
    currency: string;
}

export const ResultsPanel: React.FC<ResultsPanelProps> = ({
    data,
    inputs,
    isFetching,
    currency,
}) => {
    if (!data) return null;

    const portfolio = data.recommended_portfolio_today;

    return (
        <div
            className={`grid grid-cols-1 md:grid-cols-3 gap-4 transition-opacity duration-300 ${
                isFetching ? 'opacity-60' : 'opacity-100'
            }`}
        >
            {/* Q1: Safest Retirement */}
            <div className="bg-emerald-800 rounded-2xl p-6 text-white shadow-md relative overflow-hidden flex flex-col justify-between">
                <h3 className="text-emerald-200 text-xs font-bold uppercase tracking-wide mb-1 relative z-10">
                    Your Safest Retirement
                </h3>
                <div className="mt-2 flex items-baseline gap-2 relative z-10">
                    <span className="text-4xl font-extrabold">
                        Age {data.safest_retirement_age}
                    </span>
                </div>
                <p className="text-emerald-300 text-sm mt-1 relative z-10">
                    Target Year: {data.safest_retirement_year}
                </p>
                <div className="absolute -right-10 -bottom-10 w-48 h-48 bg-emerald-600 rounded-full opacity-50 blur-3xl pointer-events-none"></div>
            </div>

            {/* Q2: Target Savings */}
            <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-200 flex flex-col justify-between">
                <div>
                    <h3 className="text-slate-500 text-xs font-bold uppercase tracking-wide mb-1 leading-relaxed">
                        To retire at {inputs.target_retirement_age ?? '—'} you need minimum
                    </h3>
                    <div className="mt-2">
                        {data.required_savings_for_target_age === null ? (
                            <span className="text-xl font-bold text-slate-800">
                                Target unachievable
                            </span>
                        ) : data.required_savings_for_target_age === 0 ? (
                            <span className="text-2xl font-bold text-emerald-600">
                                Fully Funded!
                            </span>
                        ) : (
                            <span className="text-3xl font-extrabold text-slate-900">
                                {formatCurrency(
                                    data.required_savings_for_target_age,
                                    currency,
                                )}
                            </span>
                        )}
                    </div>
                </div>
                <p className="text-slate-400 text-xs mt-3 leading-relaxed">
                    of your savings today.
                </p>
            </div>

            {/* Q3: Required Return Today */}
            <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-200 flex flex-col justify-between">
                <div>
                    <h3 className="text-slate-500 text-xs font-bold uppercase tracking-wide mb-2 leading-relaxed">
                        To retire today you need at least{' '}
                        <span className="text-slate-900">
                            {data.required_return_to_retire_today.toFixed(1)}% return
                        </span>{' '}
                        per year with allocations to
                    </h3>
                    <div className="flex w-full h-2.5 rounded-full overflow-hidden mt-3">
                        <div
                            style={{ width: `${portfolio.equities_pct}%` }}
                            className="bg-emerald-600"
                        ></div>
                        <div
                            style={{ width: `${portfolio.bonds_pct}%` }}
                            className="bg-slate-400"
                        ></div>
                        <div
                            style={{ width: `${portfolio.cash_pct}%` }}
                            className="bg-sky-400"
                        ></div>
                    </div>
                </div>
                <div className="mt-4 space-y-2">
                    <div className="flex items-center justify-between text-sm">
                        <span className="flex items-center gap-2 font-medium text-slate-600">
                            <span className="w-3 h-3 rounded-sm bg-emerald-600"></span>
                            Equity
                        </span>
                        <span className="font-bold text-slate-900">
                            {portfolio.equities_pct}%
                        </span>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                        <span className="flex items-center gap-2 font-medium text-slate-600">
                            <span className="w-3 h-3 rounded-sm bg-slate-400"></span>
                            Fixed Income
                        </span>
                        <span className="font-bold text-slate-900">
                            {portfolio.bonds_pct}%
                        </span>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                        <span className="flex items-center gap-2 font-medium text-slate-600">
                            <span className="w-3 h-3 rounded-sm bg-sky-400"></span>
                            Cash
                        </span>
                        <span className="font-bold text-slate-900">
                            {portfolio.cash_pct}%
                        </span>
                    </div>
                </div>
            </div>
        </div>
    );
};
